// Global Instructions Rule Applied!
//
// The Allocation section of a consolidated commitment: how the commitment is
// split across the member funds of the consolidated fund. One row per member
// fund. The satellite rows are typed (amount, and optionally their own status
// and close date — an SBIC allocation closes on its SBA approval date and may
// sit pending as Committed with no date; blank = as the commitment). The
// primary fund is never typed: it takes what the satellites leave, which must
// be positive. Until a satellite allocation is effective its amount still
// counts on the primary fund. A commitment of 0 (the carry's own account) has
// no split: the save gives every member fund a pocket of 0.
//
// The rules are those of fg__save_subscription (function_fg__subscriptions.sql);
// the section replaces the "Allocate" mode of the former pop-up.

import React, { useMemo } from 'react'
import dayjs from 'dayjs'
import { Alert, Button, InputNumber, Select, Table, Tag } from 'antd'
import DatePicker from '../../../core/components/DatePicker.tsx'
import type { ColumnsType } from 'antd/es/table'

import { FormSection } from '../../../core/components/FormSection.tsx'
import HelpTip from '../../../core/components/HelpTip.tsx'
import { useNumberDisplay, useNumberFormat } from '../../../core/utils/inputNumberFormat.ts'
import type { SelectListEntry } from '../../../state_manager/data_models/select_list_entry.ts'
import {
  summariseAllocations,
  type AllocationRowDraft,
  type CommitmentCoreDraft
} from '../utils/commitmentDrafts.ts'

const DATE_FORMAT = 'MM/DD/YYYY'
const INHERIT = 'As the commitment'
const SATELLITE_STATUS_HELP =
  'A satellite allocation can close on its own (an SBIC allocation closes on its SBA approval date). Committed with ' +
  "no date = awaiting approval. Blank status / date = the consolidated commitment's."

interface Props {
  rows: AllocationRowDraft[]
  core: CommitmentCoreDraft
  statusOptions: SelectListEntry[]
  currencySymbol?: string | null
  onRowChange: (memberId: string, patch: Partial<Pick<AllocationRowDraft, 'amount' | 'status_id' | 'date_closed'>>) => void
  /** fills the satellite rows that have a default share and no amount yet */
  onApplyDefaults?: () => void
  /** opens the member fund's own commitment (its pocket) */
  onOpenPocket?: (row: AllocationRowDraft) => void
}

export function CommitmentAllocationSection({
  rows,
  core,
  statusOptions,
  currencySymbol,
  onRowChange,
  onApplyDefaults,
  onOpenPocket
}: Props): React.ReactElement {
  const currencyFmt = useNumberFormat('currency')
  const currencyText = useNumberDisplay('currency')
  const closedStatusId = useMemo(() => {
    const closed = statusOptions.find((s) => s.label === 'Closed')
    return typeof closed?.value === 'number' ? closed.value : null
  }, [statusOptions])
  const summary = useMemo(
    () => summariseAllocations(rows, core, closedStatusId, dayjs().format('YYYY-MM-DD')),
    [rows, core, closedStatusId]
  )
  const primaryName = rows.find((row) => row.isPrimary)?.fundName || 'the primary fund'
  const hasAmount = core.amount != null && core.amount > 0
  const carryAtZero = core.amount === 0 && core.is_carry_recipient
  const canApplyDefaults =
    onApplyDefaults != null &&
    hasAmount &&
    rows.some((row) => !row.isPrimary && row.defaultPct != null && !((row.amount ?? 0) > 0))

  const columns = useMemo<ColumnsType<AllocationRowDraft>>(
    () => [
      {
        title: 'Fund',
        key: 'fund',
        render: (_: unknown, row) => (
          <span className='inline-flex items-center gap-2'>
            {row.pocketId && onOpenPocket ? (
              <button
                type='button'
                className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left'
                onClick={() => onOpenPocket(row)}
              >
                {row.fundName}
              </button>
            ) : (
              row.fundName
            )}
            <Tag color={row.isPrimary ? 'blue' : 'purple'}>{row.isPrimary ? 'Primary' : 'Satellite'}</Tag>
            {!row.isPrimary && row.defaultPct != null ? (
              <span className='text-gray-500'>default {Math.round(row.defaultPct * 100)}%</span>
            ) : null}
          </span>
        )
      },
      {
        title: 'Amount',
        key: 'amount',
        align: 'right',
        width: 220,
        render: (_: unknown, row) =>
          row.isPrimary ? (
            <span className={summary.residual > 0 || !hasAmount ? undefined : 'text-red-500'}>
              {hasAmount ? `${currencySymbol ?? ''}${currencyText(summary.residual)}` : '-'}
            </span>
          ) : (
            <InputNumber
              className='w-full'
              size='small'
              aria-label={`${row.fundName} amount`}
              min={0}
              max={core.amount ?? undefined}
              prefix={currencySymbol ?? undefined}
              value={row.amount}
              onChange={(value) =>
                onRowChange(row.memberId, { amount: value == null || value === '' ? null : Number(value) })
              }
              {...currencyFmt}
            />
          )
      },
      {
        title: (
          <span>
            Status
            <HelpTip title={SATELLITE_STATUS_HELP} />
          </span>
        ),
        key: 'status',
        width: 200,
        render: (_: unknown, row) =>
          row.isPrimary ? (
            <span className='text-gray-500'>{INHERIT}</span>
          ) : (
            <Select
              className='w-full'
              size='small'
              aria-label={`${row.fundName} status`}
              allowClear
              placeholder={INHERIT}
              options={statusOptions}
              value={row.status_id ?? undefined}
              onChange={(value) => onRowChange(row.memberId, { status_id: typeof value === 'number' ? value : null })}
            />
          )
      },
      {
        title: 'Date Closed',
        key: 'date_closed',
        width: 180,
        render: (_: unknown, row) =>
          row.isPrimary ? (
            <span className='text-gray-500'>{INHERIT}</span>
          ) : (
            <DatePicker
              className='w-full'
              size='small'
              aria-label={`${row.fundName} date closed`}
              format={DATE_FORMAT}
              placeholder={INHERIT}
              value={row.date_closed ? dayjs(row.date_closed) : null}
              onChange={(value) => onRowChange(row.memberId, { date_closed: value ? value.format('YYYY-MM-DD') : null })}
            />
          )
      }
    ],
    [summary.residual, hasAmount, currencySymbol, currencyText, currencyFmt, core.amount, statusOptions, onRowChange, onOpenPocket]
  )

  return (
    <FormSection
      title='Allocation'
      titleColor='blue'
      scrollable={false}
      headerActions={
        canApplyDefaults ? (
          <Button size='small' onClick={onApplyDefaults}>
            Apply default allocation
          </Button>
        ) : null
      }
    >
      <Table<AllocationRowDraft>
        rowKey='memberId'
        dataSource={rows}
        columns={columns}
        pagination={false}
        size='small'
        scroll={{ x: 'max-content' }}
        locale={{ emptyText: <div className='text-center text-gray-500 text-sm py-2'>No member funds</div> }}
      />
      {carryAtZero ? (
        <Alert
          className='mt-2'
          type='info'
          showIcon
          message='A commitment of 0: each member fund gets a commitment of 0 that receives its carried interest.'
        />
      ) : null}
      {hasAmount && summary.residual <= 0 ? (
        <Alert
          className='mt-2'
          type='error'
          showIcon
          message={`The satellite allocations must leave a positive amount in ${primaryName}.`}
        />
      ) : null}
      {hasAmount && summary.pendingSum > 0 ? (
        <Alert
          className='mt-2'
          type='warning'
          showIcon
          message={`Until the satellite allocation closes, the full ${currencySymbol ?? ''}${currencyText(core.amount)} counts as committed to ${primaryName} for calls, fees and closed capital (${currencySymbol ?? ''}${currencyText(summary.pendingSum)} pending).`}
        />
      ) : null}
    </FormSection>
  )
}

export default CommitmentAllocationSection
